import { Loader } from '@hologram-dimension/loader';
import {
  AccountPage,
  OrganizationProvider,
  SimInventoryCommonTypes,
} from '@hologram-hyper-dashboard/components';
import { BUTTON_CLICK } from 'common-js/analytics/actionTypes';
import sendAnalyticsEvent from 'common-js/analytics/analytics';
import { getUserContextDataMemoized } from 'common-js/api/util';
import { HoloHelmet } from 'common-js/components';
import DefaultStripeElements from 'common-js/components/DefaultStripeElements';
import {
  BILLING_ACCOUNT,
  BILLING_DATA_PLANS,
  BILLING_HISTORY,
  BILLING_INVOICES,
  BILLING_ORDERS,
  BILLING_SETTINGS,
  BILLING_STATEMENTS,
  pathMatches,
  pathMatchesWithIdsReplaced,
} from 'common-js/constants/paths';
import useAppSelector from 'common-js/hooks/useAppSelector';
import useUpdateBillingAddress from 'common-js/hooks/useUpdateBillingAddress';
import {
  getBalance,
  selectBillingAddress,
  selectBillingInformation,
} from 'common-js/reducers/organization/selectors';
import { useCallback, useEffect, useMemo, type FC } from 'react';
import { browserHistory, type RouteComponentProps } from 'react-router';
import DataPlans from './DataPlans';
import Plans from './Plans';
import Account from './Account';
import History from './History';
import Invoices from './Invoices';
import Orders from './Orders';
import Statements from './Statements';

interface BillingWrapperProps extends RouteComponentProps<{}, RouterQueryParams> {}

// This whole file is kind of an anti-pattern in the react router v3 world.
// We'll make do until nextjs
const BillingWrapper: FC<BillingWrapperProps> = (props) => {
  const { router, location } = props;
  const { pathname: path } = location;

  const balance = useAppSelector(getBalance);
  const userContext = useAppSelector(getUserContextDataMemoized);
  const orgId = userContext.isInOrgContext ? userContext.orgId : userContext.userOrgId;

  const getViewFromUrl = useCallback((url) => {
    if (pathMatchesWithIdsReplaced(BILLING_SETTINGS, url)) {
      return 'settings';
    }

    return 'invoices';
  }, []);

  const view: SimInventoryCommonTypes.AccountView = getViewFromUrl(path);

  const onNavigate = useCallback(
    (url: string) => {
      // TODO: Hacky, fix with proper routing when we redo nav
      const urlSplit = url.split('/');
      const orgIdx = urlSplit.indexOf('org');
      urlSplit[orgIdx + 1] = orgId;

      const urlToUse = urlSplit.join('/');

      sendAnalyticsEvent({
        type: BUTTON_CLICK,
        data: {
          name: `SIM Details - ${getViewFromUrl(urlToUse)}`,
        },
      });

      browserHistory.push({ pathname: urlToUse });
    },
    [getViewFromUrl, orgId]
  );

  const billingAddress = useAppSelector(selectBillingAddress);
  const hasBillingAddress = !!(billingAddress && billingAddress.line1);
  const updateBillingAddress = useUpdateBillingAddress();
  const billingInformation = useAppSelector(selectBillingInformation);
  const isBillingAddressSet = billingInformation?.isBillingAddressSet ?? false;

  const component = useMemo(() => {
    if (!balance) {
      return <Loader />;
    }

    // Legacy billing
    if (balance.billingScheme !== 'STANDARD_EXTERNAL') {
      if (pathMatches(path, BILLING_ACCOUNT)) {
        return <Account {...props} />;
      }

      if (pathMatches(path, BILLING_HISTORY)) {
        return <History {...props} />;
      }

      if (pathMatches(path, BILLING_ORDERS)) {
        return <Orders {...props} />;
      }

      if (pathMatches(path, BILLING_STATEMENTS)) {
        return <Statements {...props} />;
      }

      if (pathMatches(path, BILLING_INVOICES)) {
        return <Invoices {...props} />;
      }

      if (pathMatches(path, BILLING_DATA_PLANS)) {
        return <Plans {...props} />;
      }

      return null;
    }

    // New billing
    if (pathMatches(path, BILLING_INVOICES) || pathMatches(path, BILLING_SETTINGS)) {
      return (
        <OrganizationProvider organizationId={orgId!}>
          <HoloHelmet title="Billing Invoices" />
          <AccountPage
            viewKey={view}
            className="full-height-no-padding"
            onNavigate={onNavigate}
            billingBaseUrl={BILLING_ACCOUNT}
            legacyInvoices={<Invoices {...props} onlyTable />}
            updateBillingAddress={updateBillingAddress}
            billingAddress={hasBillingAddress ? billingAddress : undefined}
            isBillingAddressSet={isBillingAddressSet}
          />
        </OrganizationProvider>
      );
    }

    if (pathMatches(path, BILLING_DATA_PLANS)) {
      return (
        <>
          <HoloHelmet title="Data Plans" />
          <div className="billing-container full-width">
            <DataPlans />
          </div>
        </>
      );
    }

    return null;
  }, [
    balance,
    path,
    props,
    orgId,
    view,
    onNavigate,
    updateBillingAddress,
    hasBillingAddress,
    billingAddress,
    isBillingAddressSet,
  ]);

  useEffect(() => {
    if (component === null) {
      if (balance.billingScheme === 'STANDARD_EXTERNAL') {
        router.push(BILLING_INVOICES);
      } else {
        router.push(BILLING_ACCOUNT);
      }
    }
  }, [balance.billingScheme, component, router]);

  return <DefaultStripeElements>{component}</DefaultStripeElements>;
};

export default BillingWrapper;
